import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import {
  RefreshCw, Users, ChevronDown, ChevronRight, BarChart3,
  TrendingUp, Calendar, Clock
} from "lucide-react";
import { toast } from "sonner";

const WORK_COLUMNS = [
  { key: "ci", label: "CI", color: "#3B82F6", bgColor: "bg-blue-100" },
  { key: "rci", label: "RCI", color: "#8B5CF6", bgColor: "bg-purple-100" },
  { key: "dtm", label: "DTM", color: "#F59E0B", bgColor: "bg-amber-100" },
  { key: "dtc", label: "DTC", color: "#EF4444", bgColor: "bg-red-100" },
  { key: "mi", label: "MI", color: "#10B981", bgColor: "bg-green-100" },
];

const EVENT_COLORS = {
  survey_submitted: "bg-blue-100 text-blue-700",
  resurvey_submitted: "bg-purple-100 text-purple-700",
  dtm_completed: "bg-amber-100 text-amber-700",
  dtc_completed: "bg-red-100 text-red-700",
  mi_completed: "bg-green-100 text-green-700"
};

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

export default function WorkActivityDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("today");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedSupervisors, setExpandedSupervisors] = useState({});

  // Determine which API to call based on role
  const getApiEndpoint = () => {
    switch (user?.role) {
      case "supervisor":
        return "/work-activity/team-stats";
      case "ci":
      case "division_incharge":
        return "/work-activity/circle-stats";
      case "package_head":
        return "/work-activity/package-stats";
      case "admin":
        return "/work-activity/admin-stats";
      default:
        return "/work-activity/team-stats";
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const endpoint = getApiEndpoint();
      const res = await api.get(endpoint, { params: { period } });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load work activity data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period, user?.role]);

  const toggleSupervisor = (supId) => {
    setExpandedSupervisors((prev) => ({
      ...prev,
      [supId]: !prev[supId],
    }));
  };

  const getDashboardTitle = () => {
    switch (user?.role) {
      case "supervisor":
        return "Team Work Activity";
      case "ci":
      case "division_incharge":
        return "Circle Work Activity";
      case "package_head":
        return "Package Work Activity";
      case "admin":
        return "All Packages Work Activity";
      default:
        return "Work Activity Dashboard";
    }
  };

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="work-activity-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            {getDashboardTitle()}
          </h1>
          <p className="text-slate-500 text-sm">CI / RCI / DTM / DTC / MI breakdown</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Period Toggle */}
          <div className="flex rounded-sm border border-slate-200 overflow-hidden">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-4 py-2 text-sm font-bold transition-colors ${
                  period === p.key
                    ? "bg-orange-500 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={fetchData}
            className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50"
          >
            <RefreshCw
              size={16}
              className={loading ? "animate-spin text-orange-500" : "text-slate-500"}
            />
          </button>
        </div>
      </div>

      {/* Grand Totals Card */}
      {data && (data.grand_totals || data.totals) && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={18} className="text-orange-500" />
            <span className="font-bold text-slate-700">
              {period === "today" ? "Today's" : period === "week" ? "This Week's" : "This Month's"} Total
            </span>
          </div>
          <div className="grid grid-cols-6 gap-3">
            {WORK_COLUMNS.map((col) => (
              <div key={col.key} className={`${col.bgColor} rounded-sm p-3 text-center`}>
                <div className="text-2xl font-black" style={{ color: col.color }}>
                  {(data.grand_totals || data.totals)?.[col.key] || 0}
                </div>
                <div className="text-xs font-bold text-slate-600">{col.label}</div>
              </div>
            ))}
            <div className="bg-slate-800 rounded-sm p-3 text-center">
              <div className="text-2xl font-black text-white">
                {(data.grand_totals || data.totals)?.total || 0}
              </div>
              <div className="text-xs font-bold text-slate-300">TOTAL</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
          <p className="text-slate-500">Loading work activity...</p>
        </div>
      ) : (
        <>
          {/* Supervisor View - Team Stats */}
          {user?.role === "supervisor" && data?.team_stats && (
            <SupervisorTable data={data} period={period} />
          )}

          {/* CI/DI View - Grouped by Supervisor */}
          {(user?.role === "ci" || user?.role === "division_incharge") && data?.supervisors && (
            <CircleTable
              data={data}
              expandedSupervisors={expandedSupervisors}
              toggleSupervisor={toggleSupervisor}
              period={period}
            />
          )}

          {/* Package Head View - By Circle */}
          {user?.role === "package_head" && data?.circles && (
            <PackageTable data={data} />
          )}

          {/* Admin View - By Package */}
          {user?.role === "admin" && data?.packages && (
            <AdminTable data={data} />
          )}
        </>
      )}
    </div>
  );
}

// Inline Work History Component (used by all tables)
function TechnicianHistoryRow({ employeeId, period, colSpan }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get(`/work-activity/technician-history/${employeeId}?period=${period}&limit=30`);
        setHistory(res.data.items || []);
      } catch {
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [employeeId, period]);

  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <div className="bg-slate-50 border-t border-b border-slate-200 px-6 py-3">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-xs py-2">
              <RefreshCw size={12} className="animate-spin" /> Loading history...
            </div>
          ) : history.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">No work recorded for this period</div>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {history.map((item) => {
                const time = item.created_at ? new Date(item.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
                const badgeClass = EVENT_COLORS[item.event_type] || "bg-slate-100 text-slate-600";
                return (
                  <div key={item.id} className="flex items-center gap-3 bg-white rounded px-3 py-1.5 border border-slate-100" data-testid={`team-history-${item.id}`}>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold shrink-0 ${badgeClass}`}>{item.label}</span>
                    <span className="text-xs font-bold text-slate-700 truncate">{item.dt_code}</span>
                    <span className="text-[11px] text-slate-400 truncate hidden sm:inline">{item.dt_name}</span>
                    <span className="text-[11px] text-slate-400 truncate">{item.feeder_code}</span>
                    <span className="text-[10px] text-slate-400 ml-auto whitespace-nowrap shrink-0">{time}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// Supervisor Table - Technician rows with expandable history
function SupervisorTable({ data, period }) {
  const [expandedTech, setExpandedTech] = useState({});
  const toggleTech = (id) => setExpandedTech(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <span className="font-bold text-slate-700">
          <Users size={16} className="inline mr-2" />
          My Team ({data.team_count} technicians)
        </span>
        <span className="text-xs text-slate-400 ml-2">Click a row to see detailed work history</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-3 font-bold text-slate-600 min-w-[180px]">Technician</th>
              {WORK_COLUMNS.map((col) => (
                <th key={col.key} className="text-center p-3 font-bold text-white w-16" style={{ backgroundColor: col.color }}>
                  {col.label}
                </th>
              ))}
              <th className="text-center p-3 font-bold text-white bg-slate-800 w-20">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.team_stats.map((tech, idx) => (
              <React.Fragment key={tech.technician_id}>
                <tr
                  className={`cursor-pointer transition-colors ${expandedTech[tech.technician_id] ? "bg-orange-50" : idx % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50 hover:bg-slate-100"}`}
                  onClick={() => tech.total > 0 && toggleTech(tech.technician_id)}
                  data-testid={`tech-row-${tech.technician_id}`}
                >
                  <td className="p-3 font-medium text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      {tech.total > 0 && (expandedTech[tech.technician_id] ? <ChevronDown size={14} className="text-orange-500" /> : <ChevronRight size={14} className="text-slate-400" />)}
                      {tech.technician_name}
                    </span>
                  </td>
                  {WORK_COLUMNS.map((col) => (
                    <td key={col.key} className="p-3 text-center font-bold">
                      <span className={tech[col.key] > 0 ? "text-slate-800" : "text-slate-300"}>{tech[col.key]}</span>
                    </td>
                  ))}
                  <td className="p-3 text-center font-black text-slate-800 bg-slate-100">{tech.total}</td>
                </tr>
                {expandedTech[tech.technician_id] && (
                  <TechnicianHistoryRow employeeId={tech.technician_id} period={period} colSpan={7} />
                )}
              </React.Fragment>
            ))}
            <tr className="bg-orange-50 font-bold">
              <td className="p-3 text-orange-700">TEAM TOTAL</td>
              {WORK_COLUMNS.map((col) => (
                <td key={col.key} className="p-3 text-center text-orange-700">{data.totals[col.key]}</td>
              ))}
              <td className="p-3 text-center text-white bg-orange-500 font-black">{data.totals.total}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Circle Table - Grouped by Supervisor (Expandable with technician history)
function CircleTable({ data, expandedSupervisors, toggleSupervisor, period }) {
  const [expandedTech, setExpandedTech] = useState({});
  const toggleTech = (id) => setExpandedTech(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
        <span className="font-bold text-slate-700">
          <Users size={16} className="inline mr-2" />
          {data.circle_name} ({data.supervisor_count} supervisors, {data.technician_count} technicians)
        </span>
        <span className="text-xs text-slate-400">Click technician to see work details</span>
      </div>
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="text-left p-3 font-bold text-slate-600 min-w-[220px]">Name</th>
              {WORK_COLUMNS.map((col) => (
                <th key={col.key} className="text-center p-3 font-bold text-white w-16" style={{ backgroundColor: col.color }}>
                  {col.label}
                </th>
              ))}
              <th className="text-center p-3 font-bold text-white bg-slate-800 w-20">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.supervisors.map((sup) => (
              <React.Fragment key={sup.supervisor_id}>
                <tr
                  className="bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors"
                  onClick={() => toggleSupervisor(sup.supervisor_id)}
                >
                  <td className="p-3 font-bold text-orange-700">
                    <span className="inline-flex items-center gap-2">
                      {expandedSupervisors[sup.supervisor_id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      {sup.supervisor_name}
                      <span className="text-xs font-normal text-orange-500">({sup.technician_count} tech)</span>
                    </span>
                  </td>
                  {WORK_COLUMNS.map((col) => (
                    <td key={col.key} className="p-3 text-center font-bold text-orange-700">{sup.totals[col.key]}</td>
                  ))}
                  <td className="p-3 text-center font-black text-white bg-orange-500">{sup.totals.total}</td>
                </tr>

                {expandedSupervisors[sup.supervisor_id] &&
                  sup.technicians.map((tech, idx) => (
                    <React.Fragment key={tech.technician_id}>
                      <tr
                        className={`cursor-pointer transition-colors ${expandedTech[tech.technician_id] ? "bg-blue-50" : idx % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50 hover:bg-slate-100"}`}
                        onClick={(e) => { e.stopPropagation(); tech.total > 0 && toggleTech(tech.technician_id); }}
                      >
                        <td className="p-3 pl-10 text-slate-600">
                          <span className="inline-flex items-center gap-1.5">
                            {tech.total > 0 && (expandedTech[tech.technician_id] ? <ChevronDown size={12} className="text-blue-500" /> : <ChevronRight size={12} className="text-slate-400" />)}
                            <span className="text-slate-400 mr-1">└</span>
                            {tech.technician_name}
                          </span>
                        </td>
                        {WORK_COLUMNS.map((col) => (
                          <td key={col.key} className="p-3 text-center">
                            <span className={tech[col.key] > 0 ? "text-slate-700" : "text-slate-300"}>{tech[col.key]}</span>
                          </td>
                        ))}
                        <td className="p-3 text-center font-bold text-slate-700 bg-slate-100">{tech.total}</td>
                      </tr>
                      {expandedTech[tech.technician_id] && (
                        <TechnicianHistoryRow employeeId={tech.technician_id} period={period} colSpan={7} />
                      )}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
            <tr className="bg-slate-800 text-white font-bold sticky bottom-0">
              <td className="p-3">CIRCLE TOTAL</td>
              {WORK_COLUMNS.map((col) => (
                <td key={col.key} className="p-3 text-center">{data.grand_totals[col.key]}</td>
              ))}
              <td className="p-3 text-center font-black bg-green-500">{data.grand_totals.total}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Package Table - By Circle
function PackageTable({ data }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <span className="font-bold text-slate-700">
          <Users size={16} className="inline mr-2" />
          {data.package_name} ({data.circle_count} circles)
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-3 font-bold text-slate-600 min-w-[180px]">Circle</th>
              <th className="text-center p-3 font-bold text-slate-600 w-20">Technicians</th>
              {WORK_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="text-center p-3 font-bold text-white w-16"
                  style={{ backgroundColor: col.color }}
                >
                  {col.label}
                </th>
              ))}
              <th className="text-center p-3 font-bold text-white bg-slate-800 w-20">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.circles.map((circle, idx) => (
              <tr key={circle.circle_name} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="p-3 font-medium text-slate-700">{circle.circle_name}</td>
                <td className="p-3 text-center text-slate-500">{circle.technician_count}</td>
                {WORK_COLUMNS.map((col) => (
                  <td key={col.key} className="p-3 text-center font-bold">
                    <span className={circle[col.key] > 0 ? "text-slate-800" : "text-slate-300"}>
                      {circle[col.key]}
                    </span>
                  </td>
                ))}
                <td className="p-3 text-center font-black text-slate-800 bg-slate-100">
                  {circle.total}
                </td>
              </tr>
            ))}
            {/* Totals Row */}
            <tr className="bg-orange-50 font-bold">
              <td className="p-3 text-orange-700">PACKAGE TOTAL</td>
              <td className="p-3 text-center text-orange-600">
                {data.circles.reduce((sum, c) => sum + c.technician_count, 0)}
              </td>
              {WORK_COLUMNS.map((col) => (
                <td key={col.key} className="p-3 text-center text-orange-700">
                  {data.grand_totals[col.key]}
                </td>
              ))}
              <td className="p-3 text-center text-white bg-orange-500 font-black">
                {data.grand_totals.total}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Admin Table - Circles grouped by Package (Expandable)
function AdminTable({ data }) {
  const [expandedPackages, setExpandedPackages] = useState(() => {
    // Expand all packages by default
    const expanded = {};
    data.packages?.forEach(pkg => {
      expanded[pkg.package_name] = true;
    });
    return expanded;
  });

  const togglePackage = (pkgName) => {
    setExpandedPackages(prev => ({
      ...prev,
      [pkgName]: !prev[pkgName]
    }));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="px-4 py-3 bg-slate-800 border-b border-slate-700">
        <span className="font-bold text-white">
          <Users size={16} className="inline mr-2" />
          All Packages ({data.package_count} packages, {data.total_circles} circles)
        </span>
      </div>
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr>
              <th className="text-left p-3 font-bold text-slate-600 min-w-[180px]">Circle</th>
              <th className="text-center p-3 font-bold text-slate-600 w-16">Tech</th>
              {WORK_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="text-center p-3 font-bold text-white w-16"
                  style={{ backgroundColor: col.color }}
                >
                  {col.label}
                </th>
              ))}
              <th className="text-center p-3 font-bold text-white bg-slate-800 w-20">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.packages.map((pkg) => (
              <React.Fragment key={pkg.package_name}>
                {/* Package Header Row (Clickable) */}
                <tr
                  className="bg-slate-800 cursor-pointer hover:bg-slate-700 transition-colors"
                  onClick={() => togglePackage(pkg.package_name)}
                >
                  <td className="p-3 font-bold text-white" colSpan={2}>
                    <span className="inline-flex items-center gap-2">
                      {expandedPackages[pkg.package_name] ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                      {pkg.package_name}
                      <span className="text-xs font-normal text-slate-400">
                        ({pkg.circle_count} circles)
                      </span>
                    </span>
                  </td>
                  {WORK_COLUMNS.map((col) => (
                    <td key={col.key} className="p-3 text-center font-bold text-slate-300">
                      {pkg.totals[col.key]}
                    </td>
                  ))}
                  <td className="p-3 text-center font-black text-white bg-orange-500">
                    {pkg.totals.total}
                  </td>
                </tr>

                {/* Circle Rows (Expandable) */}
                {expandedPackages[pkg.package_name] &&
                  pkg.circles.map((circle, idx) => (
                    <tr
                      key={circle.circle_name}
                      className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}
                    >
                      <td className="p-3 pl-10 text-slate-700">
                        <span className="text-slate-300 mr-2">└</span>
                        {circle.circle_name}
                      </td>
                      <td className="p-3 text-center text-slate-500">{circle.technician_count}</td>
                      {WORK_COLUMNS.map((col) => (
                        <td key={col.key} className="p-3 text-center font-bold">
                          <span className={circle[col.key] > 0 ? "text-slate-800" : "text-slate-300"}>
                            {circle[col.key]}
                          </span>
                        </td>
                      ))}
                      <td className="p-3 text-center font-black text-slate-800 bg-slate-100">
                        {circle.total}
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
            {/* Grand Total Row */}
            <tr className="bg-green-500 text-white font-bold sticky bottom-0">
              <td className="p-3">ORGANIZATION TOTAL</td>
              <td className="p-3 text-center">
                {data.packages.reduce((sum, p) => 
                  sum + p.circles.reduce((s, c) => s + c.technician_count, 0), 0
                )}
              </td>
              {WORK_COLUMNS.map((col) => (
                <td key={col.key} className="p-3 text-center">
                  {data.grand_totals[col.key]}
                </td>
              ))}
              <td className="p-3 text-center font-black">
                {data.grand_totals.total}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
